import { fetchUtils, DataProvider } from 'react-admin';
import { stringify } from 'query-string';
import { getBaseURL, getApiHeaders, validateApiConfig } from './config/api';

// Valideer configuratie bij startup
validateApiConfig();

// Resource mapping: frontend naam -> backend API endpoint
const resourceMapping = {
    'control': 'override',
    'planning': 'schedule', 
    'logbook': 'relais-log',
    'marketdata': 'price-data',
    'simulation': 'simulation',
    'settings': 'config'
};

const getApiResource = (resource) => {
    return resourceMapping[resource] || resource;
};

const httpClient = (url, options = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    
    // Voeg de juiste auth headers toe (API key of JWT token)
    const apiHeaders = getApiHeaders();
    Object.entries(apiHeaders).forEach(([key, value]) => {
        if (value) {
            options.headers.set(key, value);
        }
    });
    
    return fetchUtils.fetchJson(url, options);
};

export const dataProvider = {
    getList: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const { page, perPage } = params.pagination;
        const { field = 'timestamp', order = 'DESC' } = params.sort;
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify(params.filter),
        };

        const baseUrl = getBaseURL();
        
        // Speciale behandeling voor marketdata (price-data)
        if (resource === 'marketdata') {
            const url = `${baseUrl}/price-data`;
            try {
                const { json } = await httpClient(url);
                // Voeg een id toe aan elke record en hernoem value naar price
                const dataWithIds = json.data.map((item, index) => ({
                    ...item,
                    id: index + 1, // Gebruik de index als id
                    price: item.value, // Hernoem value naar price
                    value: undefined // Verwijder de oude value property
                }));
                return {
                    data: dataWithIds,
                    total: dataWithIds.length,
                };
            } catch (error) {
                console.error('Error fetching price data:', error);
                return {
                    data: [],
                    total: 0,
                };
            }
        }

        const url = `${baseUrl}/${apiResource}?${stringify(query)}`;

        try {
            const { json } = await httpClient(url);
            return {
                data: json.data,
                total: json.total,
            };
        } catch (error) {
            console.error('Error fetching list:', error);
            return {
                data: [],
                total: 0,
            };
        }
    },

    getOne: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const url = `${baseUrl}/${apiResource}/${params.id}`;
        try {
            const { json } = await httpClient(url);
            return {
                data: json,
            };
        } catch (error) {
            console.error('Error fetching one:', error);
            throw error;
        }
    },

    update: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const url = `${baseUrl}/${apiResource}/${params.id}`;
        try {
            const { json } = await httpClient(url, {
                method: 'PUT',
                body: JSON.stringify(params.data),
            });
            return {
                data: json,
            };
        } catch (error) {
            console.error('Error updating:', error);
            throw error;
        }
    },

    create: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const url = `${baseUrl}/${apiResource}`;
        try {
            const { json } = await httpClient(url, {
                method: 'POST',
                body: JSON.stringify(params.data),
            });
            return {
                data: json,
            };
        } catch (error) {
            console.error('Error creating:', error);
            throw error;
        }
    },

    delete: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const url = `${baseUrl}/${apiResource}/${params.id}`;
        try {
            const { json } = await httpClient(url, {
                method: 'DELETE',
            });
            return { data: json };
        } catch (error) {
            console.error('Error deleting:', error);
            throw error;
        }
    },

    deleteMany: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const responses = await Promise.all(
            params.ids.map(id => httpClient(`${baseUrl}/${apiResource}/${id}`, {
                method: 'DELETE',
            }))
        );
        return { data: responses.map(response => response.json) };
    },

    getMany: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        };
        const url = `${baseUrl}/${apiResource}?${stringify(query)}`;
        try {
            const { json } = await httpClient(url);
            return { data: json };
        } catch (error) {
            console.error('Error fetching many:', error);
            throw error;
        }
    },

    getManyReference: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify({
                ...params.filter,
                [params.target]: params.id,
            }),
        };
        const url = `${baseUrl}/${apiResource}?${stringify(query)}`;

        try {
            const { json } = await httpClient(url);
            return {
                data: json,
                total: json.length,
            };
        } catch (error) {
            console.error('Error fetching many reference:', error);
            return {
                data: [],
                total: 0,
            };
        }
    },

    updateMany: async (resource, params) => {
        const apiResource = getApiResource(resource);
        const baseUrl = getBaseURL();
        const responses = await Promise.all(
            params.ids.map(id => httpClient(`${baseUrl}/${apiResource}/${id}`, {
                method: 'PUT',
                body: JSON.stringify(params.data),
            }))
        );
        return { data: responses.map(response => response.json) };
    },
}; 